import { useAuthStore } from '@/stores/authStore'
import { useSecureTempStorage } from '@/composables/useSecureTempStorage'
import { useRedirectAfterLogin } from '@/utils/route'
import { queryClient } from '@/lib/vue-query'
import type { LoginRequest } from '@/types/api'
import { useRouter } from 'vue-router'
import { ErrorCodes } from '@/lib/error-handler'

/**
 * Simplified Authentication Flow Composable
 * Separates concerns and reduces complexity in useAuth.ts
 *
 * Benefits:
 * - Cleaner separation of concerns
 * - Better testability
 * - Reduced cognitive load in main auth mutations
 * - Reusable authentication logic
 */

export const useAuthFlow = () => {
  const authStore = useAuthStore()
  const router = useRouter()
  const { storeTempCredentials, clearAllTempData } = useSecureTempStorage()
  const { performRedirect } = useRedirectAfterLogin()

  /**
   * Handle 2FA required scenarios
   * Centralizes 2FA state management
   */
  const handle2FARequired = (credentials: LoginRequest) => {
    // Store credentials securely for 2FA verification
    storeTempCredentials(credentials.username, credentials.password)

    // Set auth state for 2FA flow
    authStore.setUser({
      id: 0,
      username: credentials.username,
      email: '',
      has2FA: true
    })

    authStore.setHas2fa(true)
    authStore.setIs2faVerified(false)
    authStore.setLoggedIn(true) // User is logged in but needs 2FA

    // Navigate to 2FA verification
    router.push({ name: 'verify-2fa' })
  }

  /**
   * Handle successful login completion
   * Centralizes post-login actions
   */
  const completeLogin = async () => {
    // Mark as fully authenticated
    authStore.setLoggedIn(true)

    // Fetch complete user data
    await authStore.fetchUser()

    // Invalidate stale queries
    queryClient.invalidateQueries({ queryKey: ['user'] })
    queryClient.invalidateQueries({ queryKey: ['wallet'] })

    // Clear any temporary credentials (CAPTCHA/2FA) upon successful login
    clearAllTempData()
    // Login-attempt counter reset is handled by the initiating component
    // Perform redirect based on user preferences
    performRedirect(router)
  }

  /**
   * Handle successful 2FA verification
   * Completes the 2FA authentication flow
   */
  const complete2FALogin = async () => {
    // Mark 2FA as verified
    authStore.setIs2faVerified(true)

    // Clear temporary credentials for security
    clearAllTempData()

    // Complete login process
    await completeLogin()
  }

  /**
   * Handle complete logout process
   * Centralizes cleanup logic
   */
  const performLogout = () => {
    // Clear auth state
    authStore.resetAuthState()

    // Clear temporary storage
    clearAllTempData()

    // Clear all cached queries
    queryClient.clear()

    // Redirect to login
    router.push({ name: 'login' })
  }

  /**
   * Check if error indicates 2FA requirement
   * Centralizes 2FA detection logic
   */
  const is2FARequiredError = (error: unknown): boolean => {
    if (!error || typeof error !== 'object') {
      return false
    }

    const code = (error as Record<string, unknown>)?.code
    return typeof code === 'number' && code === ErrorCodes.MISSING_TOKEN_2FA
  }

  const is2FARequiredData = (data: any): boolean => {
    return data && typeof data === 'object' && 'is_2fa_required' in data
  }

  /**
   * Handle CAPTCHA required scenarios
   * Centralizes CAPTCHA state management
   */
  const handleCaptchaRequired = (
    credentials: LoginRequest & { token_2fa?: string; captcha_token?: string }
  ) => {
    // Store credentials securely for CAPTCHA verification
    storeTempCredentials(
      credentials.username,
      credentials.password,
      credentials.token_2fa,
      credentials.captcha_token
    )

    // Navigate to login page to show CAPTCHA
    router.push({ name: 'login' })
  }

  /**
   * Check if error indicates CAPTCHA requirement
   * Centralizes CAPTCHA detection logic
   */
  const isCaptchaRequiredError = (error: unknown): boolean => {
    if (!error || typeof error !== 'object') {
      return false
    }

    const code = (error as Record<string, unknown>)?.code
    return typeof code === 'number' && code === ErrorCodes.CAPTCHA_REQUIRED
  }

  return {
    // Core flow functions
    handle2FARequired,
    handleCaptchaRequired,
    completeLogin,
    complete2FALogin,
    performLogout,

    // Utility functions
    is2FARequiredError,
    isCaptchaRequiredError,
    is2FARequiredData
  }
}
